import {faEye} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {UseInfiniteQueryResult} from '@tanstack/react-query';
import {Button, Modal, Table, TableColumnsType} from 'antd';
import {Reference} from 'rc-table/lib/interface';
import {LegacyRef, ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {Text} from '../../text/text';

import * as styles from './selected-companies-modal.scss';

type SelectedCompanyRecordType = {
    id: number;
    name: string;
    address: string | null;
    companyCode?: string;
    status: string;
};

// Structurally matches `generateResponseSchema(...)` output for the companies list.
type SelectedCompaniesPageType<RecordType extends SelectedCompanyRecordType> = {
    results: Array<RecordType>;
    next?: string | null;
    page?: number;
};

type PropsType<RecordType extends SelectedCompanyRecordType> = {
    objectId: number;
    companiesCount: number;
    title: string;
    // Name of the publication (event/promotion) the companies belong to, shown under the modal title.
    subtitle?: ReactNode;
    useCompanies: (objectId: number, enabled: boolean) => UseInfiniteQueryResult<SelectedCompaniesPageType<RecordType>>;
    renderStatus: (record: RecordType) => ReactNode;
};

export function SelectedCompaniesModal<RecordType extends SelectedCompanyRecordType>(
    props: PropsType<RecordType>
): JSX.Element {
    const {objectId, companiesCount, title, subtitle, useCompanies, renderStatus} = props;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const {data, isLoading, hasNextPage, fetchNextPage} = useCompanies(objectId, isModalOpen);

    const renderData = data?.pages.flatMap((page) => page.results) ?? [];

    const containerRef: LegacyRef<ProxyHandler<HTMLDivElement> & Reference> | null = useRef(null);
    const isNeedLoadNextPageRef = useRef<boolean>(false);

    function handleModalClose() {
        setIsModalOpen(false);
    }

    const onContainerScroll = useCallback(
        async (event: Event) => {
            if (event.target instanceof HTMLElement) {
                const {scrollHeight, clientHeight, scrollTop} = event.target;
                const maxScroll = scrollHeight - clientHeight;

                if (!isNeedLoadNextPageRef.current) {
                    isNeedLoadNextPageRef.current = maxScroll - scrollTop <= 50;

                    if (isNeedLoadNextPageRef.current && hasNextPage) {
                        await fetchNextPage();
                        isNeedLoadNextPageRef.current = false;
                    }
                }
            }
        },
        [isLoading]
    );

    useEffect(() => {
        const tableBody = containerRef?.current?.nativeElement.querySelector('.ant-table-body');

        tableBody?.addEventListener('scroll', onContainerScroll);

        return () => {
            tableBody?.removeEventListener('scroll', onContainerScroll);
        };
    }, [onContainerScroll]);

    const columns: TableColumnsType<RecordType> = [
        {
            title: <Locale stringKey="POSTS__SELECTED_COMPANIES__COMPANY" />,
            width: '560px',
            render: (_value: unknown, record: RecordType) => (
                <div className={styles.SelectedCompaniesModal_companyCell}>
                    <div className={styles.SelectedCompaniesModal_companyCellName}>
                        <Text block>{record.name}</Text>
                        <Text block lightest>
                            {record.companyCode}
                        </Text>
                    </div>
                    <Text block small>
                        {record.address}
                    </Text>
                </div>
            ),
        },
        {
            align: 'center',
            title: <Locale stringKey="POSTS__SELECTED_COMPANIES__STATUS" />,
            width: '120px',
            render: (_value: unknown, record: RecordType) => renderStatus(record),
        },
    ];

    return (
        <>
            <Button
                className={styles.SelectedCompaniesModal_companiesButton}
                icon={<FontAwesomeIcon icon={faEye} />}
                onClick={() => setIsModalOpen(true)}
                type="text"
            >
                {companiesCount}
            </Button>
            <Modal
                centered
                closable={false}
                destroyOnClose
                footer={
                    <div className={styles.SelectedCompaniesModal_modalFooter}>
                        <Text block className={styles.SelectedCompaniesModal_companiesCount}>
                            <Locale
                                stringKey="COMPANY__MULTISELECT__SELECTED_COUNT"
                                valueMap={{count: companiesCount}}
                            />
                        </Text>
                        <Button onClick={handleModalClose} type="primary">
                            <Locale stringKey="POPUP__BUTTON__CLOSE" />
                        </Button>
                    </div>
                }
                onCancel={handleModalClose}
                open={isModalOpen}
                title={
                    <div className={styles.SelectedCompaniesModal_title}>
                        <Text block>{title}</Text>
                        {subtitle && (
                            <Text block secondary>
                                {subtitle}
                            </Text>
                        )}
                    </div>
                }
                width={862}
            >
                <Table<RecordType>
                    className={styles.SelectedCompaniesModal_scroll}
                    columns={columns}
                    dataSource={renderData}
                    loading={isLoading}
                    pagination={false}
                    ref={containerRef}
                    rowKey="id"
                    scroll={{y: 500, x: true}}
                />
            </Modal>
        </>
    );
}
